import { Suspense, useRef } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams } from 'react-router-dom';
import { lazy } from '@/lib/lazy-route';
import { RootRouteErrorFallback } from '@/router/RootRouteErrorFallback';
import { AppLayout } from '@/components/layout/AppLayout';
import { SupportChatBubble } from '@/components/support/SupportChatBubble';
import { SimpleAiJobBanner, SimpleAiJobTracker } from '@/components/eduflow/simple/SimpleAiJobBanner';
import { SpaRouteAnnouncer } from '@/components/accessibility/SpaRouteAnnouncer';
import { GlobalSimplifiedText } from '@/components/accessibility/GlobalSimplifiedText';
import ReadingMask from '@/components/accessibility/ReadingMask';
import SpeakOnSelection from '@/components/accessibility/SpeakOnSelection';
import GlobalImageHoverAudio from '@/components/accessibility/GlobalImageHoverAudio';
import { CookieConsentBanner } from '@/components/consent/CookieConsentBanner';
import { InstitutionAppThemeLayout } from '@/components/layout/InstitutionAppTheme';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { InstitutionFeatureGate } from '@/components/institution/InstitutionFeatureGate';
import { INSTITUTION_MENU_FLAGS } from '@/lib/institution-menu-flags';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { useLanguageStore, type Language } from '@/stores/language.store';

const LANG_CODES = new Set<string>(['fr', 'en', 'lb', 'it', 'es', 'uk', 'ru', 'pt', 'de', 'ar', 'tr']);

function LanguageRedirect() {
  const { lang, '*': rest } = useParams<{ lang: string; '*': string }>();
  const setLanguage = useLanguageStore((s) => s.setLanguage);
  const isValid = lang != null && LANG_CODES.has(lang);
  const applied = useRef(false);

  if (isValid && !applied.current) {
    applied.current = true;
    setLanguage(lang as Language);
  }

  const target = isValid ? (rest ? `/${rest}` : '/') : '/';
  return <Navigate to={target} replace />;
}

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const InstitutionRegisterPage = lazy(() => import('@/pages/auth/InstitutionRegisterPage'));

// Dashboard
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const PodcastDashboardPage = lazy(() => import('@/pages/dashboard/PodcastDashboardPage'));
const ProducerAssignmentsPage = lazy(() => import('@/pages/dashboard/ProducerAssignmentsPage'));

// Podcasts
const PodcastsPage = lazy(() => import('@/pages/podcasts/PodcastsPage'));
const EpisodesPage = lazy(() => import('@/pages/podcasts/EpisodesPage'));
const EpisodeEditorPage = lazy(() => import('@/pages/podcasts/EpisodeEditorPage'));
const PodcastDocumentImportPage = lazy(() => import('@/pages/podcasts/PodcastDocumentImportPage'));
const PodcastPublicPage = lazy(() => import('@/pages/podcasts/PodcastPublicPage'));
const PodcastLearnerPlayPage = lazy(() => import('@/pages/podcasts/PodcastLearnerPlayPage'));
const SimplePodcastPage = lazy(() => import('@/pages/podcasts/simple/SimplePodcastPage'));
const SimpleEpisodeEditorPage = lazy(() => import('@/pages/podcasts/simple/SimpleEpisodeEditorPage'));

const TrainerWebinarsPage = lazy(() => import('@/pages/webinars/TrainerWebinarsPage'));
const EnterpriseWebinarsPage = lazy(() => import('@/pages/webinars/EnterpriseWebinarsPage'));
const WebinarRoomPage = lazy(() => import('@/pages/webinars/WebinarRoomPage'));

// EduFlow
const EduFlowDesignerPage = lazy(() => import('@/pages/eduflow/EduFlowDesignerPage'));
const EduFlowPlayerPage = lazy(() => import('@/pages/eduflow/EduFlowPlayerPage'));
const EduFlowParticipantPage = lazy(() => import('@/pages/eduflow/EduFlowParticipantPage'));
const EduFlowHubRedirect = lazy(() => import('@/pages/eduflow/EduFlowHubRedirect'));
const EduFlowListPage = lazy(() => import('@/pages/eduflow/EduFlowListPage'));
const EduFlowPresenterPage = lazy(() => import('@/pages/eduflow/EduFlowPresenterPage'));
const SimpleStructurePage = lazy(() => import('@/pages/eduflow/simple/SimpleStructurePage'));
const SimpleChapterBuilderPage = lazy(() => import('@/pages/eduflow/simple/SimpleChapterBuilderPage'));

// Store
const StoreIndexPage = lazy(() => import('@/pages/store/StoreIndexPage'));

// Admin
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'));
const AdminInstitutionDetailPage = lazy(() => import('@/pages/admin/AdminInstitutionDetailPage'));

// Institution dashboard
const InstitutionPage = lazy(() => import('@/pages/institution/InstitutionPage'));

// Marketing
const HomePage = lazy(() => import('@/pages/marketing/HomePage'));
const PricingPage = lazy(() => import('@/pages/marketing/PricingPageV2'));
const AboutPage = lazy(() => import('@/pages/marketing/AboutPageV2'));
const ContactPage = lazy(() => import('@/pages/marketing/ContactPageV2'));
const AccessibilityPage = lazy(() => import('@/pages/marketing/AccessibilityPageV2'));
const BlogPage = lazy(() => import('@/pages/marketing/BlogPage'));
const UseCasesPage = lazy(() => import('@/pages/marketing/UseCasesPageV3'));
const PartnersPage = lazy(() => import('@/pages/marketing/PartnersPageV2'));
const PrivacyPage = lazy(() => import('@/pages/marketing/PrivacyPageV2'));
const TermsPage = lazy(() => import('@/pages/marketing/TermsPageV2'));
const CookiesPage = lazy(() => import('@/pages/marketing/CookiesPageV2'));
const PublicHelpPage = lazy(() => import('@/pages/marketing/PublicHelpPage'));
const LeBrefPage = lazy(() => import('@/pages/marketing/LeBrefPage'));

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));

// Learner
const LearnerPage = lazy(() => import('@/pages/learner/LearnerPage'));

// Profile
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));

// Learners (trainer view)
const LearnersPage = lazy(() => import('@/pages/learners/LearnersPage'));
const LearnerDashboardPage = lazy(() => import('@/pages/learners/LearnerDashboardPage'));

// Media
const VideosPage = lazy(() => import('@/pages/media/VideosPage'));
const VideoEditPage = lazy(() => import('@/pages/media/VideoEditPage'));
const SoundsPage = lazy(() => import('@/pages/media/SoundsPage'));
const ImagesPage = lazy(() => import('@/pages/media/ImagesPage'));
const DocumentsPage = lazy(() => import('@/pages/media/DocumentsPage'));

// Analytics & help
const AnalyticsPage = lazy(() => import('@/pages/analytics/AnalyticsPage'));
const HelpPage = lazy(() => import('@/pages/help/HelpPage'));

// Institution settings
const GroupsPage = lazy(() => import('@/pages/institution/GroupsPage'));
const BrandingPage = lazy(() => import('@/pages/institution/BrandingPage'));
const LandingPageEditorPage = lazy(() => import('@/pages/institution/LandingPageEditorPage'));
const WebhooksPage = lazy(() => import('@/pages/institution/WebhooksPage'));

// Error
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner fullScreen />}>{children}</Suspense>
);

/** Chat support global : présent sur toutes les vues sauf pages publiques / auth (logique dans le composant). */
function RootWithSupportChat() {
  return (
    <>
      <SpaRouteAnnouncer />
      <GlobalSimplifiedText />
      <ReadingMask />
      <SpeakOnSelection />
      <GlobalImageHoverAudio />
      <Outlet />
      <SimpleAiJobTracker />
      <SimpleAiJobBanner />
      <SupportChatBubble />
      <CookieConsentBanner />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <RootWithSupportChat />,
    errorElement: <RootRouteErrorFallback />,
    children: [
  // Language prefix redirect: /fr, /en/pricing, /ar/about, etc.
  {
    path: ':lang/*',
    element: <LanguageRedirect />,
  },
  {
    path: ':lang',
    element: <LanguageRedirect />,
  },

  // Public routes with header/footer
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/',
        element: <SuspenseWrapper><HomePage /></SuspenseWrapper>,
      },
      {
        path: '/pricing',
        element: <SuspenseWrapper><PricingPage /></SuspenseWrapper>,
      },
      {
        path: '/about',
        element: <SuspenseWrapper><AboutPage /></SuspenseWrapper>,
      },
      {
        path: '/contact',
        element: <SuspenseWrapper><ContactPage /></SuspenseWrapper>,
      },
      {
        path: '/aide',
        element: <SuspenseWrapper><PublicHelpPage /></SuspenseWrapper>,
      },
      {
        path: '/accessibility',
        element: <SuspenseWrapper><AccessibilityPage /></SuspenseWrapper>,
      },
      {
        path: '/le-bref',
        element: <SuspenseWrapper><LeBrefPage /></SuspenseWrapper>,
      },
      {
        path: '/blog',
        element: <SuspenseWrapper><BlogPage /></SuspenseWrapper>,
      },
      {
        path: '/store',
        element: <SuspenseWrapper><StoreIndexPage /></SuspenseWrapper>,
      },
      {
        path: '/use-cases',
        element: <SuspenseWrapper><UseCasesPage /></SuspenseWrapper>,
      },
      {
        path: '/partners',
        element: <SuspenseWrapper><PartnersPage /></SuspenseWrapper>,
      },
      {
        path: '/privacy',
        element: <SuspenseWrapper><PrivacyPage /></SuspenseWrapper>,
      },
      {
        path: '/terms',
        element: <SuspenseWrapper><TermsPage /></SuspenseWrapper>,
      },
      {
        path: '/cookies',
        element: <SuspenseWrapper><CookiesPage /></SuspenseWrapper>,
      },
      {
        path: '/login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
      },
      {
        path: '/register',
        element: <SuspenseWrapper><RegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/establishment/register',
        element: <SuspenseWrapper><InstitutionRegisterPage /></SuspenseWrapper>,
      },
      {
        path: '/forgot-password',
        element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
      },
      {
        path: '/reset-password',
        element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
      },
    ],
  },

  // Public EduFlow routes
  {
    path: '/podcasts/public/p/:podcastId',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/public',
    element: <SuspenseWrapper><PodcastPublicPage /></SuspenseWrapper>,
  },
  {
    path: '/podcasts/learner',
    element: <SuspenseWrapper><PodcastLearnerPlayPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/flows/:flowId/public',
    element: <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>,
  },
  {
    path: '/eduflow/participant/:globalToken',
    element: <SuspenseWrapper><EduFlowParticipantPage /></SuspenseWrapper>,
  },
  {
    path: '/f/:code',
    element: <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>,
  },

  // Protected routes (require auth)

  // EduFlow designer & player — fullscreen (no sidebar)
  {
    path: '/eduflow/edit/:flowId',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowDesignerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/webinars/live/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/webinars/register/:joinCode',
    element: (
      <InstitutionAppThemeLayout>
        <SuspenseWrapper><WebinarRoomPage /></SuspenseWrapper>
      </InstitutionAppThemeLayout>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/play',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EduFlowPlayerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/eduflow/flows/:flowId/present',
    element: (
      <ProtectedRoute requiredRoles={['teacher', 'admin', 'institution_admin', 'resp_formation']}>
        <SuspenseWrapper><EduFlowPresenterPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Podcast episode editor — fullscreen (no sidebar)
  {
    path: '/podcasts/episodes/new',
    element: (
      <ProtectedRoute>
        <Navigate to="/podcasts/episodes?new=1" replace />
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/episodes/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><EpisodeEditorPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/podcasts/:podcastId/import-document',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><PodcastDocumentImportPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Video editor — fullscreen (no sidebar)
  {
    path: '/videos/:id',
    element: (
      <ProtectedRoute>
        <SuspenseWrapper><VideoEditPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Admin panel — fullscreen (independent layout, no AppLayout sidebar)
  {
    path: '/admin',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <SuspenseWrapper><AdminPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/institutions/:institutionId',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <SuspenseWrapper>
          <AdminInstitutionDetailPage />
        </SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Learner dashboard — fullscreen (independent layout)
  {
    path: '/learner',
    element: (
      <ProtectedRoute requiredRoles={['learner', 'student', 'teacher', 'resp_formation', 'collaborateur']}>
        <SuspenseWrapper><LearnerPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },

  // Institution dashboard — fullscreen (independent layout)
  {
    path: '/institution',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/institution/learners/:participantId',
    element: (
      <ProtectedRoute
        requiredRoles={[
          'institution',
          'institution_admin',
          'drh',
          'rh',
          'teacher',
          'resp_formation',
        ]}
      >
        <SuspenseWrapper><InstitutionPage /></SuspenseWrapper>
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: <SuspenseWrapper><DashboardPage /></SuspenseWrapper>,
      },
      {
        path: 'dashboard/podcast/:podcastId',
        element: <SuspenseWrapper><PodcastDashboardPage /></SuspenseWrapper>,
      },
      {
        path: 'producer-assignments',
        element: (
          <ProtectedRoute requiredRoles={['teacher', 'resp_formation', 'institution_admin']}>
            <SuspenseWrapper><ProducerAssignmentsPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'profile',
        element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper>,
      },

      // Liste des formations (formateur / rôles création)
      {
        path: 'eduflow/list',
        element: <SuspenseWrapper><EduFlowListPage /></SuspenseWrapper>,
      },
      {
        path: 'eduflow/simple/:flowId/structure',
        element: <SuspenseWrapper><SimpleStructurePage /></SuspenseWrapper>,
      },
      {
        path: 'eduflow/simple/:flowId/chapter/:chapterId',
        element: <SuspenseWrapper><SimpleChapterBuilderPage /></SuspenseWrapper>,
      },

      // Ancienne URL /eduflow → redirection (établissement → onglet flows, sinon liste formations)
      {
        path: 'eduflow',
        element: <SuspenseWrapper><EduFlowHubRedirect /></SuspenseWrapper>,
      },

      // Podcasts (list pages only — editor is fullscreen outside AppLayout)
      {
        path: 'podcasts',
        element: <SuspenseWrapper><PodcastsPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/simple/:podcastId',
        element: <SuspenseWrapper><SimplePodcastPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/simple/:podcastId/episodes/:episodeId',
        element: <SuspenseWrapper><SimpleEpisodeEditorPage /></SuspenseWrapper>,
      },
      {
        path: 'podcasts/episodes',
        element: <SuspenseWrapper><EpisodesPage /></SuspenseWrapper>,
      },

      {
        path: 'webinars',
        element: (
          <ProtectedRoute
            requiredRoles={[
              'teacher',
              'resp_formation',
              'collaborateur',
              'collaborator',
              'manager',
              'institution_admin',
            ]}
          >
            <SuspenseWrapper><TrainerWebinarsPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'webinars/enterprise',
        element: (
          <ProtectedRoute
            requiredRoles={['institution', 'institution_admin', 'drh', 'rh', 'resp_formation']}
          >
            <InstitutionFeatureGate flagKey={INSTITUTION_MENU_FLAGS.webinars_enterprise}>
              <SuspenseWrapper><EnterpriseWebinarsPage /></SuspenseWrapper>
            </InstitutionFeatureGate>
          </ProtectedRoute>
        ),
      },

      // Learners
      {
        path: 'learners/:participantId',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <SuspenseWrapper><LearnerDashboardPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: 'learners',
        element: (
          <ProtectedRoute
            requiredRoles={['teacher', 'resp_formation', 'collaborateur', 'manager', 'institution_admin', 'admin']}
          >
            <SuspenseWrapper><LearnersPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },

      // Analytics
      {
        path: 'analytics',
        element: <SuspenseWrapper><AnalyticsPage /></SuspenseWrapper>,
      },

      // Media library
      {
        path: 'videos',
        element: <SuspenseWrapper><VideosPage /></SuspenseWrapper>,
      },
      {
        path: 'sounds',
        element: <SuspenseWrapper><SoundsPage /></SuspenseWrapper>,
      },
      {
        path: 'images',
        element: <SuspenseWrapper><ImagesPage /></SuspenseWrapper>,
      },
      {
        path: 'documents',
        element: <SuspenseWrapper><DocumentsPage /></SuspenseWrapper>,
      },

      // Help
      {
        path: 'help',
        element: <SuspenseWrapper><HelpPage /></SuspenseWrapper>,
      },

      // Institution
      {
        path: 'groups',
        element: <SuspenseWrapper><GroupsPage /></SuspenseWrapper>,
      },
      {
        path: 'branding',
        element: <SuspenseWrapper><BrandingPage /></SuspenseWrapper>,
      },
      {
        path: 'landing-page',
        element: <SuspenseWrapper><LandingPageEditorPage /></SuspenseWrapper>,
      },
      {
        path: 'webhooks',
        element: <SuspenseWrapper><WebhooksPage /></SuspenseWrapper>,
      },
      // Settings
      {
        path: 'settings',
        element: <SuspenseWrapper><SettingsPage /></SuspenseWrapper>,
      },
    ],
  },

  // 404
  {
    path: '*',
    element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
  },
    ],
  },
]);

export function AppRouter() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

export default AppRouter;
